// import React, { createContext, useState, useContext, useCallback } from "react";
// import APICall from "../../Utility/APICall";
// import { paths } from "../../Utility/Constants";
// import { useAuth } from "../../Utility/AuthProvider";
// import { useUser } from "../../Utility/UserProvider";

// // Create the context
// const UnitDetailsContext = createContext();

// // Create a provider component
// export const UnitDetailsProvider = ({ children }) => {
//   //   const { user } = useUser();
//   //   console.log("User Data -", user);
//   const { jwt } = useAuth();
//   const [unitDetails, setUnitDetails] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   //   console.log("JWT - --", jwt);
//   // Function to fetch unit details

//   // Use useCallback to stabilize fetchUnitDetails
//   const fetchUnitDetails = useCallback(async (unitId, jwt) => {
//     setLoading(true);
//     try {
//       const response = await APICall(
//         "GET",
//         paths.unit.view,
//         undefined,
//         { unit_id: unitId },
//         jwt
//       );
//       setUnitDetails(response.response);
//     } catch (error) {
//       setError(error);
//     } finally {
//       setLoading(false);
//     }
//   }, []);

//   // Function to update unit details
//   const updateUnitDetails = async (updatedDetails, jwt) => {
//     setLoading(true);
//     try {
//       const response = await APICall(
//         "PUT",
//         paths.unit.update,
//         updatedDetails,
//         undefined,
//         jwt
//       );
//       setUnitDetails(response.response);
//     } catch (error) {
//       setError(error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <UnitDetailsContext.Provider
//       value={{
//         unitDetails,
//         loading,
//         error,
//         fetchUnitDetails,
//         updateUnitDetails,
//       }}
//     >
//       {children}
//     </UnitDetailsContext.Provider>
//   );
// };

// // Custom hook to use the context
// export const useUnitDetails = () => useContext(UnitDetailsContext);

import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useRef,
  useEffect,
} from "react";
import APICall from "../../Utility/APICall";
import { paths } from "../../Utility/Constants";
import { useAuth } from "../../Utility/AuthProvider";
// import { useUser } from "../../Utility/UserProvider"; // Not used in this context, can keep or remove if it's there

// Create the context
const UnitDetailsContext = createContext();

// Create a provider component
export const UnitDetailsProvider = ({ children }) => {
  const { jwt } = useAuth();

  // State for the currently displayed SINGLE unit's details
  const [unitDetails, setUnitDetails] = useState(null);
  // Loading state specific to fetching/updating a SINGLE unit
  const [loading, setLoading] = useState(false);
  // Error state specific to fetching/updating a SINGLE unit
  const [error, setError] = useState(null);

  // --- Caching Mechanism for SINGLE Unit Details ---
  // Stores individual unit details by unitId: { 'unitId1': { data: unitObject1, timestamp: Date.now() }, ... }
  const unitDetailCache = useRef({});
  const CACHE_LIFETIME = 5 * 60 * 1000; // 5 minutes in milliseconds for individual unit details

  // Function to fetch details for a SINGLE unit by ID
  // It checks the cache first, then makes an API call if not found or forced refresh.
  const fetchUnitDetails = useCallback(
    async (unitId, forceRefresh = false) => {
      console.log(
        "Fetching unit details for from fetchunitdetails context:",
        unitId
      );

      if (!unitId) {
        console.warn(
          "fetchUnitDetails called without a unitId. Exiting early."
        );
        setError("Unit ID is required to fetch details.");
        setUnitDetails(null); // Clear previous details if ID is invalid
        return null;
      }

      // 1. Check Cache
      const cachedEntry = unitDetailCache.current[unitId];
      if (
        !forceRefresh &&
        cachedEntry &&
        Date.now() - cachedEntry.timestamp < CACHE_LIFETIME
      ) {
        console.log(`Serving unit details for ${unitId} from cache.`);
        setUnitDetails(cachedEntry.data); // Update state from cache
        setLoading(false); // Ensure loading is off if served from cache
        setError(null);
        return cachedEntry.data;
      }

      // 2. Fetch from API if not in cache or forced refresh
      setLoading(true); // Start loading indicator
      setError(null); // Clear previous errors
      try {
        const response = await APICall(
          "GET",
          paths.unit.view, // Correct endpoint for fetching a SINGLE unit
          undefined, // body
          { unit_id: unitId }, // options for query parameters
          jwt
        );

        if (response.status === "success" && response.response) {
          console.log(
            `Workspaceed unit details for ${unitId} from API:`,
            response.response
          );
          const fetchedUnit = response.response;

          // Store in cache with timestamp
          unitDetailCache.current[unitId] = {
            data: fetchedUnit,
            timestamp: Date.now(),
          };
          setUnitDetails(fetchedUnit); // Update state
          return fetchedUnit;
        } else {
          const errorMessage =
            response.message || `Failed to fetch unit details for ${unitId}.`;
          console.error(`API Error fetching unit ${unitId}:`, errorMessage);
          setError(errorMessage);
          setUnitDetails(null);
          return null;
        }
      } catch (apiError) {
        console.error(`Error fetching unit ${unitId} (catch block):`, apiError);
        const errorMessage =
          apiError.message ||
          "An unexpected error occurred while fetching unit details.";
        setError(errorMessage);
        setUnitDetails(null);
        return null;
      } finally {
        setLoading(false); // End loading indicator
      }
    },
    [jwt]
  ); // Dependency on jwt for API call

  useEffect(() => {
    console.log("❌❌❌ UnitDetailsContext error:", error);
  }, [error]);

  // Function to update unit details
  // After update, invalidate or update the cache for that specific unit.
  const updateUnitDetails = useCallback(
    async (unitId, updatedDetails) => {
      if (!unitId) {
        console.error("Unit ID is required to update details.");
        setError("Unit ID is required to update details.");
        return null;
      }
      if (!jwt) {
        setError("Authentication token not available.");
        return null;
      }

      setLoading(true);
      setError(null);
      try {
        const payloadWithId = { ...updatedDetails, unit_id: unitId }; // Ensure unit_id is in the payload

        const response = await APICall(
          "PUT",
          paths.unit.update,
          payloadWithId,
          undefined,
          jwt
        );

        if (response.status === "success" && response.response) {
          console.log(
            `Unit ${unitId} updated successfully:`,
            response.response
          );
          const updatedUnit = response.response;

          // Update the context's unitDetails state if this is the currently viewed unit
          if (
            unitDetails?.entity_id === unitId ||
            unitDetails?.unit_id === unitId
          ) {
            setUnitDetails(updatedUnit);
          }

          // Update the cache for this specific unit
          unitDetailCache.current[unitId] = {
            data: updatedUnit,
            timestamp: Date.now(), // Update timestamp as data is fresh
          };
          return updatedUnit;
        } else {
          const errorMessage =
            response.message || `Failed to update unit details for ${unitId}.`;
          console.error(`API Error updating unit ${unitId}:`, errorMessage);
          setError(errorMessage);
          return null;
        }
      } catch (apiError) {
        console.error(`Error updating unit ${unitId} (catch block):`, apiError);
        const errorMessage =
          apiError.message ||
          "An unexpected error occurred while updating unit details.";
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [jwt, unitDetails]
  ); // Dependency on jwt and unitDetails to potentially update the displayed unit

  // Function to clear cache for a specific unit (e.g., after deletion)
  const clearUnitFromCache = useCallback((unitId) => {
    if (unitDetailCache.current[unitId]) {
      delete unitDetailCache.current[unitId];
      console.log(`Cache cleared for unit ${unitId}.`);
    }
  }, []);

  // Function to reset all unit details data (e.g., on logout or when leaving a unit view)
  const resetUnitDetails = useCallback(() => {
    // Renamed for clarity within this context
    setUnitDetails(null);
    setLoading(false);
    setError(null);
    // Note: This does NOT clear the entire unitDetailCache, only the currently active 'unitDetails'.
    // If you want to clear the entire cache, use resetUnitDetailCache directly.
  }, []);

  // Optional: Function to clear the entire single unit detail cache
  const resetUnitDetailCache = useCallback(() => {
    unitDetailCache.current = {}; // Clear all cached individual unit details
    console.log("Entire single unit detail cache cleared.");
  }, []);

  // Function to force clear cache and refetch
  const forceRefreshUnitDetails = useCallback(
    async (unitId) => {
      if (unitDetailCache.current[unitId]) {
        delete unitDetailCache.current[unitId];
        console.log(`Cache forcefully cleared for unit ${unitId}.`);
      }
      return await fetchUnitDetails(unitId, true);
    },
    [fetchUnitDetails]
  );

  return (
    <UnitDetailsContext.Provider
      value={{
        unitDetails,
        loading, // Loading state for single unit operations
        error, // Error state for single unit operations
        fetchUnitDetails,
        updateUnitDetails,
        clearUnitFromCache,
        resetUnitDetails, // For clearing the currently displayed unit details
        resetUnitDetailCache, // For clearing the entire cache of single unit details
        forceRefreshUnitDetails,
        clearUnitFromCache,
      }}
    >
      {children}
    </UnitDetailsContext.Provider>
  );
};

// Custom hook to use the context
export const useUnitDetails = () => {
  const context = useContext(UnitDetailsContext);
  if (context === undefined) {
    throw new Error("useUnitDetails must be used within a UnitDetailsProvider");
  }
  return context;
};
